import { DatePicker } from '@mui/lab';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Typography,
  useEventCallback,
} from '@mui/material';
import {
  AccordionGeneralityForm,
  ForeignForm,
  SearchPersonTableVM,
} from 'civil-status/view-models/accordion-generality-form';
import ListGender, { ID_GENDERS } from 'shell/components/shared/gender';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import Foreign from '../../../shared/foreign';
import Italian from '../../../shared/italian';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ItemVM } from 'shell/view-models/item';
import CivilStatusList from 'shell/components/shared/civil-status-list';
import { noop } from 'shell/utils/noop';
import { Checkbox } from '@mui/material';
import { DateTime } from 'luxon';
import { LastStepVM, ParentVM, SectionVM } from 'civil-status/view-models/step-form';
import { CodeSubject } from 'civil-status/constants/code-subject-section';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import BaseIntCit from './base-int-cit';
import { KeySections, KeySubjects } from 'civil-status/constants/keys-subjects-section';
import {
  initialConditionsItem,
  initialValuesParent,
} from 'civil-status/constants/initial-values-step';
import Twins from '../shared/twins';
import NationalityList, {
  CODE_NAZIONALITA_ITALIA,
  NationalityVM,
} from 'shell/components/shared/nationality-list';
import { PmiButton } from 'shell/components/shared/button';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ConsultazioneANSCResponse } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { DESCRIPTION_ITALIAN_NATION, ID_ITALIAN_NATION } from 'shell/constants/id-italian-nation';
import { transformerResearchIdAnscList } from 'civil-status/utils/transformer-research-id-ansc-list';
import TableSearchPerson from '../../table-search-person';
import { ID_REASON_IMPEDIMENT } from 'civil-status/constants/id-reason-impedimenti';
import ListAtti from 'civil-status/components/shared/list-atti';
import ListUfcDelegate, { ItemUFCVM } from '../list/list-ufc-delegate';
import ListAnsc from 'civil-status/components/shared/list-ansc';
import { ID_OTHER_UFC_DELEGATE } from 'civil-status/constants/id-other-ufc-delegate';
import FormBaseAvv from '../base-avv';
import { IdResidence } from 'civil-status/constants/id-residence';
import { CivilStatusID } from 'shell/constants/civil-status';
import { withInterpreteCheck } from 'civil-status/utils/with-interprete-check';
import { withIsSignatoryCheck } from 'civil-status/utils/with-is-signatory-check';
import Figlio from './figlio';
import { isMaggiorenne } from 'shell/utils/is-maggiorenne';
import { CodeFormatBirthDate } from 'civil-status/constants/code-format-birth-date';

interface FormGeneralityBaseProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onClean: (mantainDataGenarality?: AccordionGeneralityForm) => void;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllIntNasCivilStatus: (values?: string) => void;
  controllDecedutoCivilStatus: (values?: string) => void;
  controllIsChangeSurnameName: (isChangeSurnameName?: boolean) => void;
  configuration?: string;
  codeSubject?: string;
  flagDichiarante?: string;
  flagFirmatario?: string;
  tipoSoggetto?: string;
  dateFormationAct?: string;
  keySubject?: string;
  requiredOnlyNameSurname?: boolean;
  requiredOnlyName?: boolean;
  nothingRequired?: boolean;
  isBaseDec?: boolean;
  isStepFromEmergency?: boolean;
}

const FormGeneralityBaseComponent = ({
  formContext,
  disabled = false,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  setDataInterprete,
  controllIntNasCivilStatus,
  controllDecedutoCivilStatus,
  flagDichiarante,
  flagFirmatario,
  tipoSoggetto,
  dateFormationAct,
  keySubject = '',
  configuration = '',
  codeSubject = '',
  requiredOnlyNameSurname = false,
  requiredOnlyName = false,
  nothingRequired = false,
  isBaseDec = false,
  isStepFromEmergency = false,
  onClean,
}: FormGeneralityBaseProps): React.ReactElement => {
  const [researchIdAnscListData, setResearchIdAnscListData] = useState<
    Array<SearchPersonTableVM> | undefined
  >(undefined);

  const isDichiarante: boolean = useMemo(() => {
    return flagDichiarante === '1';
  }, [flagDichiarante]);

  const isFirmatario: boolean = useMemo(() => {
    return flagFirmatario === '1';
  }, [flagFirmatario]);

  const isIntestatario: boolean = useMemo(() => {
    return tipoSoggetto === 'intestatari';
  }, [tipoSoggetto]);

  const isBaseScatto = useMemo(() => {
    return configuration === ConfigurationType.BASESCATTO;
  }, [configuration]);

  const isBase02Dec = useMemo(() => {
    return configuration === ConfigurationType.BASE02DEC;
  }, [configuration]);

  const isSanitario = useMemo(() => {
    return configuration === ConfigurationType.SANIT01;
  }, [configuration]);

  const isProcura = useMemo(() => {
    return configuration === ConfigurationType.PROCUR01;
  }, [configuration]);

  const isIntCit = useMemo(() => {
    return configuration === ConfigurationType.INTCIT;
  }, [configuration]);

  const isIntCit2 = useMemo(() => {
    return configuration === ConfigurationType.INTCIT2;
  }, [configuration]);

  const isIntCit3 = useMemo(() => {
    return configuration === ConfigurationType.INTCIT3;
  }, [configuration]);

  const isIntCit4 = useMemo(() => {
    return configuration === ConfigurationType.INTCIT4;
  }, [configuration]);

  const isBaseG = useMemo(() => {
    return configuration === ConfigurationType.BASEG;
  }, [configuration]);

  const isBaseUc = useMemo(() => {
    return configuration === ConfigurationType.BASEUC;
  }, [configuration]);

  const isBasePerd = useMemo(() => {
    return configuration === ConfigurationType.BASEPERD;
  }, [configuration]);

  const isBaseDir = useMemo(() => {
    return configuration === ConfigurationType.BASEDIR;
  }, [configuration]);

  const isConiopFi = useMemo(() => {
    return configuration === ConfigurationType.CONIOPFI;
  }, [configuration]);

  const isBaseOpFi = useMemo(() => {
    return configuration === ConfigurationType.BASEOPFI;
  }, [configuration]);

  const isRichUcopf = useMemo(() => {
    return configuration === ConfigurationType.RICH_UCOPF;
  }, [configuration]);

  const isOffiCiv = useMemo(() => {
    return configuration === ConfigurationType.OFFI_CIV;
  }, [configuration]);

  const isOffiCitt = useMemo(() => {
    return configuration === ConfigurationType.OFFI_CITT;
  }, [configuration]);

  const isOffiRel = useMemo(() => {
    return configuration === ConfigurationType.OFFI_REL;
  }, [configuration]);

  const isOffiTras = useMemo(() => {
    return configuration === ConfigurationType.OFFI_TRAS;
  }, [configuration]);

  const isSogmina = useMemo(() => {
    return configuration === ConfigurationType.SOGMINA;
  }, [configuration]);

  const isBaseAnni = useMemo(() => {
    return configuration === ConfigurationType.BASEANNI;
  }, [configuration]);

  const isOffiAcatt = useMemo(() => {
    return configuration === ConfigurationType.OFFI_ACATT;
  }, [configuration]);

  const isBase02 = useMemo(() => {
    return configuration === ConfigurationType.BASE02;
  }, [configuration]);

  const isBase02A = useMemo(() => {
    return configuration === ConfigurationType.BASE02A;
  }, [configuration]);

  const isBase14 = useMemo(() => {
    return configuration === ConfigurationType.BASE14;
  }, [configuration]);

  const isBase02opfi = useMemo(() => {
    return configuration === ConfigurationType.BASE02OPFI;
  }, [configuration]);

  const isSegrCiv = useMemo(() => {
    return configuration === ConfigurationType.SEGR_CIV;
  }, [configuration]);

  const isPericCiv = useMemo(() => {
    return configuration === ConfigurationType.PERIC_CIV;
  }, [configuration]);

  const isNeosnNoInt = useMemo(() => {
    return configuration === ConfigurationType.NEOSNOINT;
  }, [configuration]);

  const isNeosnNoIntP = useMemo(() => {
    return configuration === ConfigurationType.NEOSNOINTP;
  }, [configuration]);

  const isBaseNoIntR = useMemo(() => {
    return configuration === ConfigurationType.BASENOINTR;
  }, [configuration]);

  const isBaseNoIntP = useMemo(() => {
    return configuration === ConfigurationType.BASENOINTP;
  }, [configuration]);

  const requiredDataBorn: boolean = useMemo(() => {
    return isBase02 || isBase02opfi || isBase02A;
  }, [isBase02, isBase02opfi, isBase02A]);

  const isBaseAvv3: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEAVV3;
  }, [configuration]);

  const isBaseAvv4: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEAVV4;
  }, [configuration]);

  const isBaseResMi: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASERESMI;
  }, [configuration]);

  const isFiglioTrna: boolean = useMemo(() => {
    return configuration === ConfigurationType.FIGLIOTRNA;
  }, [configuration]);

  const isBaseAnmd: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEANMD;
  }, [configuration]);

  const isWithInterpreteCheck = useMemo(() => {
    return withInterpreteCheck(isDichiarante, configuration);
  }, [isDichiarante, configuration]);

  const birthDateIsMaggiorenne: boolean = useMemo(() => {
    return isMaggiorenne(generality?.birthDate);
  }, [generality?.birthDate]);

  useEffect(() => {
    if (isWithInterpreteCheck) {
      setDataInterprete(generality?.isWithInterprete);
    }
  }, [generality?.isWithInterprete, isWithInterpreteCheck]);

  const requiredRic: boolean = useMemo(() => {
    return (
      configuration === ConfigurationType.RIC1 ||
      configuration === ConfigurationType.RIC2 ||
      configuration === ConfigurationType.RIC3 ||
      configuration === ConfigurationType.RIC4 ||
      configuration === ConfigurationType.RIC5 ||
      configuration === ConfigurationType.RIC6 ||
      configuration === ConfigurationType.RIC7
    );
  }, [configuration]);

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldItalianResidenceChange = useCallback(
    (key, value) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianResidenceChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsForeignChange = useCallback(
    (value) => {
      onChangeGenerality('foreign', {
        ...(value || {}),
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignResidenceChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreignResidence', {
        ...(generality || {}).foreignResidence,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsForeignResidenceChange = useCallback(
    (value) => {
      onChangeGenerality('foreignResidence', {
        ...(value || {}),
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    if (keySubject?.includes(CodeSubject.DECEDUTO)) {
      // settare il sesso anche del coniuge e dell'unito civilmente
      formContext.setValues({
        ...(formContext.values || {}),
        [KeySubjects.keyConiuge]: {
          ...formContext.values[KeySubjects.keyConiuge],
          generality: {
            ...formContext.values[KeySubjects.keyConiuge]?.generality,
            gender:
              event.target.value === ID_GENDERS.FEMMINA
                ? ID_GENDERS.MASCHIO
                : event.target.value === ID_GENDERS.MASCHIO
                ? ID_GENDERS.FEMMINA
                : ID_GENDERS.SCONOSCIUTO,
          },
        },
        [KeySubjects.keyUnitoCivilmente]: {
          ...formContext.values[KeySubjects.keyUnitoCivilmente],
          generality: {
            ...formContext.values[KeySubjects.keyUnitoCivilmente]?.generality,
            gender: event.target.value,
          },
        },
        [keySubject]: {
          ...formContext.values[keySubject],
          generality: {
            ...formContext.values[keySubject]?.generality,
            gender: event.target.value,
          },
        },
      });
    } else {
      onChangeGenerality('gender', event.target.value);
    }
  });

  const handleListRoloChange = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('rolo', item?.id);
  });

  const handleListConditionsChange = useEventCallback((item?: ItemVM) => {
    onChangeFieldsGenerality({
      ...(generality || {}),
      conditionsItem: item,
      reasonImpediment: '',
    });
  });

  const handleListConditionsSignatoryChange = useEventCallback((item?: ItemVM) => {
    onChangeFieldsGenerality({
      ...(generality || {}),
      conditionsItemSignatory: item,
      reasonImpedimentSignatory: '',
    });
  });

  const handleListReasonLossCitizenshipChange = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('reasonLossCitizenship', item);
  });

  const handleListRoloSecretaryChange = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('roloSecretary', item?.id);
  });

  useEffect(() => {
    if (generality?.conditionsItem?.id?.toString() === ID_REASON_IMPEDIMENT) {
      return;
    }

    onChangeGenerality('reasonImpediment', undefined);
  }, [generality?.conditionsItem]);

  const idValorePadre: string = useMemo(() => {
    // Ottiene le chiavi dei valori del values
    const keysForm = Object.keys(formContext.values || {});

    // Trova la chiave del campo che richiede la gestione dell'interprete
    const keyObjectWithRitualReligious =
      keysForm.find((itemKey) =>
        Boolean((formContext.values[itemKey] as SectionVM)?.ritualReligious?.id)
      ) || '';

    return (
      (
        formContext.values[keyObjectWithRitualReligious] as SectionVM
      )?.ritualReligious?.id?.toString() || ''
    );
  }, [formContext.values]);

  // const handleListReasonImpedimentChange = useEventCallback((item?: ItemVM) => {
  //   onChangeGenerality('reasonImpedimentItem', item);
  // });

  const handleCivilStatusListChange = useEventCallback((item?: ItemVM) => {
    if (isBaseScatto) {
      // controllo per attivare altre sezioni in questa configurazione
      controllIntNasCivilStatus(item?.id?.toString() || '');
    }

    if (keySubject === KeySubjects.keyDeceduto) {
      // Reset del coniuge
      formContext.setValues({
        ...(formContext.values || {}),
        [keySubject]: {
          ...(formContext.values[keySubject] || {}),
          generality: {
            ...((formContext.values[keySubject] || {}) as ParentVM).generality,
            civilStatusItem: item,
          },
        } as ParentVM,
        [KeySubjects.keyConiuge]: {
          ...(formContext.values[KeySubjects.keyConiuge] || {}),
          generality: {
            ...initialValuesParent.generality,
          },
        },
      });

      controllDecedutoCivilStatus(item?.id?.toString());
    }

    onChangeGenerality('civilStatusItem', item);
  });

  const onChangeSurnameName = useEventCallback((newIsChangeSurnameName?: boolean) => {
    if (newIsChangeSurnameName) {
      formContext.setValues({
        ...(formContext.values || {}),
        [keySubject]: {
          ...(formContext.values[keySubject] || {}),
          generality: {
            ...((formContext.values[keySubject] || {}) as ParentVM).generality,
            isChangeSurnameName: newIsChangeSurnameName,
          },
        } as ParentVM,
        [KeySubjects.keyIntest2]: {
          ...(formContext.values[KeySubjects.keyIntest2] || {}),
          generality: {
            ...((formContext.values[keySubject] || {}) as ParentVM).generality,
            isRequiredFromOtherAccordion: true,
            idSubject: undefined,
          },
        } as ParentVM,
      });
    } else {
      formContext.setValues({
        ...(formContext.values || {}),
        [keySubject]: {
          ...(formContext.values[keySubject] || {}),
          generality: {
            ...((formContext.values[keySubject] || {}) as ParentVM).generality,
            isChangeSurnameName: newIsChangeSurnameName,
          },
        } as ParentVM,
        [KeySubjects.keyIntest2]: {
          ...(formContext.values[KeySubjects.keyIntest2] || {}),
          generality: {
            ...initialValuesParent.generality,
            isRequiredFromOtherAccordion: false,
            idSubject: undefined,
          },
        },
      });
    }
  });

  const handleNationalityChange = useEventCallback(
    (nationality: NationalityVM | null | undefined) => {
      onChangeFieldsGenerality({
        nationality,
        foreignResidence: {
          ...(generality?.foreignResidence || {}),
          municipalityAire:
            nationality?.idAnpr && nationality?.idAnpr?.toString() !== CODE_NAZIONALITA_ITALIA
              ? undefined
              : generality?.foreignResidence?.municipalityAire,
        },
      });
    }
  );

  const handleOtherCitizenshipListChange = useEventCallback(
    (state: NationalityVM | null | undefined) => {
      onChangeGenerality('otherCitizenship', state);
    }
  );

  const onChangeListRelationship = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('relationship', item?.id || '');
  });

  const handleListUfcDelegate = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('ufcDelegate', item?.id || '');
  });

  const handleFormatBirthDate = useEventCallback((item?: ItemVM) => {
    onChangeFieldsGenerality({
      ...(generality || {}),
      formatBirthDate: item,
      birthDate: null,
      age: undefined,
    });
  });

  const viewsDataBorn = useMemo(() => {
    switch (generality?.formatBirthDate?.id?.toString()) {
      case CodeFormatBirthDate.SenzaGiorno:
        return ['year', 'month'] as const;
      case CodeFormatBirthDate.SenzaGiornoMese:
        return ['year'] as const;

      default:
        return ['year', 'month', 'day'] as const;
    }
  }, [generality?.formatBirthDate]);

  const handleListUfcDelegateOffiAcatt = useEventCallback((item?: ItemUFCVM) => {
    onClean({
      ufcDelegate: item?.id?.toString() || '',
      name: item?.id !== ID_OTHER_UFC_DELEGATE ? item?.name || '' : '',
      surname: item?.id !== ID_OTHER_UFC_DELEGATE ? item?.surname || '' : '',
      idSubject: generality?.idSubject,
    });
  });

  const onChangeResidence = useEventCallback((typeResidence: IdResidence) => {
    onChangeFieldsGenerality({
      typeResidence,
      italianResidence: {
        location: '',
        province: '',
        municipality: null,
      },
      foreignResidence: {
        state: null,
        province: '',
        municipality: null,
      },
      address: '',
    });
  });

  const disabledResearchIdAnsc: boolean = useMemo(() => {
    return !Boolean(
      generality?.surname &&
        generality?.name &&
        generality?.gender &&
        generality?.birthDate &&
        generality?.birthDate.isValid &&
        (generality?.italian?.municipality || generality?.foreign?.state)
    );
  }, [
    generality?.surname,
    generality?.name,
    generality?.gender,
    generality?.birthDate,
    generality?.italian?.municipality,
    generality?.foreign?.state,
  ]);

  const [researchIdAnscList, getResearchIdAnscList] = useHttpFetch<
    HttpResponse<ConsultazioneANSCResponse>,
    Array<SearchPersonTableVM>
  >(`${__ACT_API_URL__}/consultazione/verifica-registrazione-ansc`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerResearchIdAnscList,
  });

  const onResearchIdAnsc = useEventCallback(() => {
    getResearchIdAnscList({
      data: {
        cognome: generality?.surname?.trim() || undefined,
        nome: generality?.name?.trim() || undefined,
        sesso: generality?.gender || undefined,
        dataNascita: generality?.birthDate
          ? generality?.birthDate.toFormat('yyyy-MM-dd')
          : undefined,
        idComuneNascita: generality?.italian?.municipality?.code || undefined,
        idProvNascita: generality?.italian?.municipality?.idProvince || undefined,
        siglaProvNascita: generality?.italian?.municipality?.province || undefined,
        comuneNascita: generality?.italian?.municipality?.description || undefined,
        statoNascita: generality?.italian?.municipality
          ? DESCRIPTION_ITALIAN_NATION
          : generality?.foreign?.state?.value || undefined,
        idStatoNascita: generality?.italian?.municipality
          ? ID_ITALIAN_NATION
          : generality?.foreign?.state?.id
          ? Number.parseInt(generality?.foreign?.state?.id, 10)
          : undefined,
      },
    });
  });

  useEffect(() => {
    if (researchIdAnscList.data) {
      setResearchIdAnscListData(researchIdAnscList.data as Array<SearchPersonTableVM>);
    }
  }, [researchIdAnscList.data]);

  const onVisibilityPressAnsc = useEventCallback((valuesSearchPerson?: SearchPersonTableVM) => {
    setResearchIdAnscListData(undefined);

    onChangeGenerality('idAnsc', valuesSearchPerson?.idAnsc || '');
  });

  const disabledNameSurname: boolean = useMemo(() => {
    if (!isOffiAcatt) {
      return false;
    }

    if (generality?.ufcDelegate === ID_OTHER_UFC_DELEGATE) {
      return false;
    }

    return true;
  }, [generality?.ufcDelegate, isOffiAcatt]);

  const valuesDatiSepar: SectionVM = useMemo(() => {
    return formContext.values[KeySections.keyDatiSepar] || {};
  }, [formContext.values[KeySections.keyDatiSepar]]);

  useEffect(() => {
    if (valuesDatiSepar.configuration !== ConfigurationType.SEPAR08) {
      return;
    }

    formContext.setValues({
      ...formContext.values,
      [KeySubjects.keySposo]: {
        ...(formContext.values[KeySubjects.keySposo] || {}),
        flagFirmatario: valuesDatiSepar?.isHusband ? '1' : '0',
      } as ParentVM,
      [KeySubjects.keySposa]: {
        ...(formContext.values[KeySubjects.keySposa] || {}),
        flagFirmatario: valuesDatiSepar?.isWife ? '1' : '0',
      } as ParentVM,
    });
  }, [valuesDatiSepar?.isHusband, valuesDatiSepar?.isWife]);

  const isSignatoryCheck: boolean = useMemo(() => {
    return withIsSignatoryCheck(configuration);
  }, [configuration]);

  const dateFormatCompleteBaseAnmd =
    isBaseAnmd && !generality?.formatBirthDate?.id && !!generality?.birthDate;

  return (
    <Grid container spacing={2} item>
      {codeSubject === CodeSubject.INT_NAS ? (
        <Twins
          generality={generality}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          onChangeGenerality={onChangeGenerality}
          disabled={isBaseAnmd || disabled}
          errorsGenerality={errorsGenerality}
          touchedGenerality={touchedGenerality}
        />
      ) : undefined}
      {isFiglioTrna ? (
        <Figlio
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          onChangeGenerality={onChangeGenerality}
          disabled={isBaseAnmd || disabled}
          errorsGenerality={errorsGenerality}
          touchedGenerality={touchedGenerality}
          generality={generality}
        />
      ) : undefined}
      {isIntCit4 ? (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="isChangeSurnameName"
                checked={generality?.isChangeSurnameName || false}
                onChange={() => {
                  onChangeSurnameName(!generality?.isChangeSurnameName);
                }}
                inputProps={{ 'aria-label': 'Cambio cognome e/o nome' }}
                disabled={isBaseAnmd || disabled}
              />
            }
            label="Cambio cognome e/o nome"
          />
        </Grid>
      ) : undefined}
      {isBaseAvv3 || isBaseAvv4 ? (
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={`Foro di appartenenza ${nothingRequired || isBaseAvv3 ? '' : '*'}`}
            aria-label="Foro di appartenenza"
            id="lawyerForum"
            fullWidth
            disabled={isBaseAnmd || disabled}
            inputProps={{
              maxLength: '80',
            }}
            helperText={errorsGenerality?.lawyerForum}
            error={touchedGenerality?.lawyerForum && Boolean(errorsGenerality?.lawyerForum)}
            value={generality?.lawyerForum || ''}
            onChange={(event) => {
              onChangeGenerality('lawyerForum', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      {isOffiCiv || isOffiRel || isOffiTras ? (
        <Grid item xs={12} md={6}>
          <ListAtti
            label={
              isOffiRel
                ? 'Officiante rito religioso'
                : isOffiTras
                ? 'Trasmissione atto'
                : 'Ufficiale di stato civile celebrante il rito'
            }
            idDominio={isOffiRel || isOffiTras ? '16' : '15'}
            value={generality?.ufcDelegate || ''}
            onChange={handleListUfcDelegate}
            disabled={isBaseAnmd || disabled}
            error={Boolean(errorsGenerality?.ufcDelegate) && touchedGenerality?.ufcDelegate}
            required={!nothingRequired}
          />
        </Grid>
      ) : undefined}
      {isOffiCitt ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Ufficiale dello stato civile delegato"
            idDominio="121"
            value={generality?.ufcDelegate || ''}
            onChange={handleListUfcDelegate}
            error={Boolean(errorsGenerality?.ufcDelegate) && touchedGenerality?.ufcDelegate}
            required
            withoutId={['5']}
          />
        </Grid>
      ) : undefined}
      {isOffiAcatt ? (
        <Grid item xs={12} md={6}>
          <ListUfcDelegate
            label="Ministro di culto"
            value={generality?.ufcDelegate || ''}
            onChange={handleListUfcDelegateOffiAcatt}
            disabled={isBaseAnmd || disabled || !Boolean(idValorePadre)}
            error={Boolean(errorsGenerality?.ufcDelegate) && touchedGenerality?.ufcDelegate}
            required={!(Boolean(generality?.name) && Boolean(generality?.surname))}
            idValorePadre={idValorePadre}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={9}>
        <PmiTextField
          label={`Cognome ${nothingRequired || requiredOnlyName ? '' : '*'}`}
          aria-label="Cognome"
          id="surname"
          fullWidth
          disabled={isBaseAnmd || disabled || disabledNameSurname}
          helperText={errorsGenerality?.surname}
          error={touchedGenerality?.surname && Boolean(errorsGenerality?.surname)}
          value={generality?.surname || ''}
          inputProps={{
            maxLength: '80',
          }}
          onChange={(event) => {
            onChangeGenerality('surname', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          label={`Nome ${nothingRequired ? '' : '*'}`}
          aria-label="Nome"
          id="name"
          fullWidth
          disabled={isBaseAnmd || disabled || disabledNameSurname}
          inputProps={{
            maxLength: '80',
          }}
          helperText={errorsGenerality?.name}
          error={touchedGenerality?.name && Boolean(errorsGenerality?.name)}
          value={generality?.name || ''}
          onChange={(event) => {
            onChangeGenerality('name', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListGender
          required={
            requiredDataBorn
              ? true
              : !(requiredOnlyNameSurname || requiredOnlyName || nothingRequired || isRichUcopf)
          }
          value={generality?.gender || ''}
          onChange={handleListGenderChange}
          disabled={isBaseAnmd || disabled}
          error={Boolean(errorsGenerality?.gender) && touchedGenerality?.gender}
          helperText={errorsGenerality?.gender}
          withoutUnknown
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CivilStatusList
          label="Stato civile"
          aria-label="Stato civile"
          value={generality?.civilStatusItem?.id?.toString() || ''}
          onChange={noop}
          onChangeItem={handleCivilStatusListChange}
          error={touchedGenerality?.civilStatusItem && Boolean(errorsGenerality?.civilStatusItem)}
          helperText={errorsGenerality?.civilStatusItem}
          disabled={isBaseAnmd || disabled}
          filterItems={(items) => {
            // Filtro gli elementi in base al tipo di configurazione
            if (
              !isNeosnNoInt &&
              !isNeosnNoIntP &&
              !(configuration === ConfigurationType.PERIC_CIV) &&
              !(configuration === ConfigurationType.NEOSPOS)
            ) {
              return items;
            }

            return items.filter(
              (item) =>
                item.id?.toString() !== CivilStatusID.coniugato &&
                item.id?.toString() !== CivilStatusID.unitoCivilmente
            );
          }}
          required={isBaseDec || isBase02Dec || isBaseScatto}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID Anpr"
          aria-label="ID Anpr"
          id="idAnpr"
          fullWidth
          disabled
          helperText={errorsGenerality?.idAnpr}
          error={touchedGenerality?.idAnpr && Boolean(errorsGenerality?.idAnpr)}
          value={generality?.idAnpr || ''}
          onChange={(event) => {
            onChangeGenerality('idAnpr', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Identificativo unico nazionale"
          aria-label="Identificativo unico nazionale"
          id="idNationalIdentifier"
          fullWidth
          disabled
          helperText={errorsGenerality?.idNationalIdentifier}
          error={
            touchedGenerality?.idNationalIdentifier &&
            Boolean(errorsGenerality?.idNationalIdentifier)
          }
          value={generality?.idNationalIdentifier || ''}
          onChange={(event) => {
            onChangeGenerality('idNationalIdentifier', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID Ansc Soggetto"
          aria-label="ID Ansc Soggetto"
          id="idAnsc"
          fullWidth
          disabled
          helperText={errorsGenerality?.idAnsc}
          error={touchedGenerality?.idAnsc && Boolean(errorsGenerality?.idAnsc)}
          value={generality?.idAnsc || ''}
          onChange={(event) => {
            onChangeGenerality('idAnsc', event.target.value);
          }}
        />
      </Grid>
      {isIntestatario ? (
        <Grid item xs={12} md={3}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            onClick={onResearchIdAnsc}
            disabled={isBaseAnmd || ((disabled || disabledResearchIdAnsc) && !isStepFromEmergency)}
          >
            Ricerca ID Ansc Soggetto
          </PmiButton>
        </Grid>
      ) : undefined}
      {researchIdAnscListData ? (
        <Grid item xs={12}>
          {(researchIdAnscListData || []).length > 0 ? (
            <TableSearchPerson
              tableBody={researchIdAnscListData}
              onSearchPerson={noop}
              onVisibilityPressAnsc={onVisibilityPressAnsc}
              isAnsc
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessun elemento trovato con la ricerca effettuata.
            </Typography>
          )}
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {isSogmina || isBase02A || isBaseAnni || isBaseAnmd ? (
        <Fragment>
          <Grid item xs={12} md={3}>
            <ListAnsc
              label="Formato data"
              idDominio="125"
              value={
                !!generality?.formatBirthDate?.id?.toString()
                  ? generality?.formatBirthDate?.id?.toString()
                  : dateFormatCompleteBaseAnmd
                  ? '1'
                  : ''
              }
              onChange={handleFormatBirthDate}
              error={
                Boolean(errorsGenerality?.formatBirthDate) && touchedGenerality?.formatBirthDate
              }
              disabled={
                disabled ||
                (isBaseAnmd && !!generality?.formatBirthDate?.id?.toString() == true) ||
                dateFormatCompleteBaseAnmd
              }
              itemsToAdd={
                [{ id: CodeFormatBirthDate.SoloAnni, value: 'Solo età' }] as Array<ItemVM>
              }
            />
          </Grid>
          {generality?.formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni ? (
            <Grid item xs={12} md={3}>
              <PmiTextField
                label="Età (anni)"
                aria-label="Età (anni)"
                name="age"
                id="age"
                fullWidth
                type="number"
                value={generality?.age || ''}
                onChange={(event) => onChangeGenerality('age', event.target.value)}
                disabled={
                  disabled || (isBaseAnmd && !!generality?.formatBirthDate?.id?.toString() == true)
                }
                error={touchedGenerality?.age && Boolean(errorsGenerality?.age)}
                helperText={errorsGenerality?.age}
              />
            </Grid>
          ) : undefined}
        </Fragment>
      ) : undefined}
      <Grid item xs={12} md={3}>
        <DatePicker
          label={`Data nascita ${
            generality?.formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni
              ? ''
              : requiredDataBorn
              ? '*'
              : requiredOnlyNameSurname || requiredOnlyName || nothingRequired || isRichUcopf
              ? ''
              : '*'
          }`}
          aria-label="Data nascita"
          views={viewsDataBorn}
          value={generality?.birthDate || null}
          onChange={(date) => {
            if (!date) {
              onChangeGenerality('birthDate', null);
              return;
            }

            let normalizedDate = date;

            switch (generality?.formatBirthDate?.id?.toString()) {
              case CodeFormatBirthDate.SenzaGiorno:
                // 01/MM/AAAA
                normalizedDate = date.set({ day: 1 });
                break;

              case CodeFormatBirthDate.SenzaGiornoMese:
              case CodeFormatBirthDate.SoloAnni:
                // 01/01/AAAA
                normalizedDate = date.set({ month: 1, day: 1 });
                break;

              default:
                break;
            }

            onChangeGenerality('birthDate', normalizedDate);
          }}
          disabled={
            isBaseAnmd ||
            disabled ||
            generality?.formatBirthDate?.id?.toString() === CodeFormatBirthDate.SoloAnni
          }
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="birthDate"
              fullWidth
              helperText={errorsGenerality?.birthDate}
              error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
            />
          )}
        />
      </Grid>
      {isBaseAnmd ? (
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data morte"
            aria-label="Data morte"
            value={generality?.dateDeath || null}
            onChange={(date) => {
              onChangeGenerality('dateDeath', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateDeath"
                fullWidth
                helperText={errorsGenerality?.dateDeath}
                error={touchedGenerality?.dateDeath && Boolean(errorsGenerality?.dateDeath)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="fiscalCode"
          id="fiscalCode"
          fullWidth
          value={generality?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => onChangeGenerality('fiscalCode', event.target.value)}
          inputProps={{
            maxLength: '16',
          }}
          disabled={isBaseAnmd || disabled}
          error={touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode)}
          helperText={errorsGenerality?.fiscalCode}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <NationalityList
          value={generality?.nationality || null}
          onChange={handleNationalityChange}
          required={
            !(requiredOnlyNameSurname || requiredOnlyName || nothingRequired || requiredRic)
          }
          disabled={disabled}
          id="nationality"
          error={
            (touchedGenerality?.nationality as unknown as FormikTouched<NationalityVM>) &&
            Boolean(errorsGenerality?.nationality as FormikErrors<NationalityVM>)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeignBorn"
            value={generality?.isForeignBorn || 'false'}
            onChange={() => {
              onChangeFieldsGenerality({
                isForeignBorn: !generality?.isForeignBorn,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isBaseAnmd || disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isBaseAnmd || disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeignBorn ? (
        <Italian
          disabledComponent={disabled}
          values={generality?.italian}
          onChange={onFieldItalianChange}
          onChanges={onFieldsItalianChange}
          fromDate={generality?.birthDate || null}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={`Comune di nascita ${
            (requiredOnlyNameSurname || requiredOnlyName || nothingRequired) && !requiredDataBorn
              ? ''
              : '*'
          }`}
          labelProvince={`Provincia di nascita ${
            (requiredOnlyNameSurname || requiredOnlyName || nothingRequired) && !requiredDataBorn
              ? ''
              : '*'
          }`}
        />
      ) : (
        <Foreign
          disabledComponent={disabled}
          values={generality?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreign as unknown as FormikTouched<ForeignForm>}
          required={
            (requiredOnlyNameSurname || requiredOnlyName || nothingRequired) && !requiredDataBorn
              ? false
              : true
          }
          isBirth
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {isBaseResMi ? undefined : (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Residenza"
              id="typeResidence"
              value={generality?.typeResidence || IdResidence.Italian}
            >
              <FormControlLabel
                value={IdResidence.Italian}
                control={<Radio />}
                label="Residenza Italia"
                disabled={disabled}
                onChange={() => onChangeResidence(IdResidence.Italian)}
              />
              <FormControlLabel
                value={IdResidence.Foreign}
                control={<Radio />}
                label="Residenza Estero"
                disabled={disabled}
                onChange={() => onChangeResidence(IdResidence.Foreign)}
              />
              <FormControlLabel
                value={IdResidence.NotKnown}
                control={<Radio />}
                label="Residenza non nota"
                disabled={isBaseAnmd || disabled}
                onChange={() => onChangeResidence(IdResidence.NotKnown)}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      {generality?.typeResidence === IdResidence.Italian || isBaseResMi ? (
        <Italian
          disabledComponent={disabled}
          values={generality?.italianResidence}
          onChange={onFieldItalianResidenceChange}
          onChanges={onFieldsItalianResidenceChange}
          fromDate={dateFormationAct ? DateTime.fromISO(dateFormationAct) : null}
          errors={errorsGenerality?.italianResidence as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italianResidence as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={`Comune di residenza ${
            requiredOnlyNameSurname ||
            requiredOnlyName ||
            nothingRequired ||
            requiredRic ||
            isBase02Dec
              ? ''
              : '*'
          }`}
          labelProvince={`Provincia di residenza ${
            requiredOnlyNameSurname ||
            requiredOnlyName ||
            nothingRequired ||
            requiredRic ||
            isBase02Dec
              ? ''
              : '*'
          }`}
          isResidence
        />
      ) : undefined}
      {generality?.typeResidence === IdResidence.Foreign ? (
        <Foreign
          disabledComponent={disabled}
          values={generality?.foreignResidence}
          onChange={onFieldForeignResidenceChange}
          onChanges={onFieldsForeignResidenceChange}
          errors={errorsGenerality?.foreignResidence as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreignResidence as unknown as FormikTouched<ForeignForm>}
          labelState="Stato estero di residenza"
          required={
            !(
              requiredOnlyNameSurname ||
              requiredOnlyName ||
              nothingRequired ||
              requiredRic ||
              isBase02Dec
            )
          }
          isResidence
          fromDate={dateFormationAct ? DateTime.fromISO(dateFormationAct) : null}
          disabledAire={
            Boolean(generality.nationality?.idAnpr) &&
            generality.nationality?.idAnpr?.toString() !== CODE_NAZIONALITA_ITALIA
          }
        />
      ) : undefined}
      {generality?.typeResidence === IdResidence.Italian ||
      generality?.typeResidence === IdResidence.Foreign ? (
        <Grid item xs={12}>
          <PmiTextField
            aria-label="Indirizzo di residenza"
            label="Indirizzo di residenza"
            id="address"
            name="address"
            fullWidth
            disabled={disabled}
            helperText={errorsGenerality?.address}
            error={touchedGenerality?.address && Boolean(errorsGenerality?.address)}
            type="text"
            variant="outlined"
            value={generality?.address || ''}
            onChange={(event) => {
              onChangeGenerality('address', event.target.value);
            }}
            inputProps={{
              maxLength: '240',
            }}
          />
        </Grid>
      ) : undefined}
      {generality?.typeResidence === IdResidence.NotKnown ? (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="isUntraceable"
                checked={generality.isUntraceable || false}
                onChange={() => {
                  onChangeGenerality('isUntraceable', !generality.isUntraceable);
                }}
                inputProps={{ 'aria-label': 'Irreperibile' }}
              />
            }
            label="Irreperibile"
          />
        </Grid>
      ) : undefined}
      {isBaseDir ? (
        <Grid item xs={12} md={3}>
          <DatePicker
            label={`Data inizio residenza ${
              requiredOnlyNameSurname || requiredOnlyName || nothingRequired ? '' : '*'
            }`}
            aria-label="Data inizio residenza"
            value={generality?.dateStartResidence || null}
            onChange={(date) => {
              onChangeGenerality('dateStartResidence', date);
            }}
            disabled={isBaseAnmd || disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateStartResidence"
                fullWidth
                helperText={errorsGenerality?.dateStartResidence}
                error={
                  touchedGenerality?.dateStartResidence &&
                  Boolean(errorsGenerality?.dateStartResidence)
                }
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isSanitario ? (
        <Grid item xs={12} md={4}>
          <ListAnsc
            label="Ruolo dichiarante"
            idDominio="34"
            required={!(requiredOnlyNameSurname || requiredOnlyName || nothingRequired)}
            value={generality?.rolo || ''}
            onChange={handleListRoloChange}
            disabled={isBaseAnmd || disabled}
            error={Boolean(errorsGenerality?.rolo) && touchedGenerality?.rolo}
          />
        </Grid>
      ) : undefined}
      {isProcura ? (
        <Grid item xs={12}>
          <PmiTextField
            aria-label="Dati essenziali della procura speciale"
            label={`Dati essenziali della procura speciale ${
              requiredOnlyNameSurname || requiredOnlyName || nothingRequired ? '' : '*'
            }`}
            id="attorney"
            name="attorney"
            fullWidth
            disabled={isBaseAnmd || disabled}
            helperText={errorsGenerality?.attorney}
            error={touchedGenerality?.attorney && Boolean(errorsGenerality?.attorney)}
            type="text"
            variant="outlined"
            value={generality?.attorney || ''}
            onChange={(event) => {
              onChangeGenerality('attorney', event.target.value);
            }}
            inputProps={{
              maxLength: '240',
            }}
          />
        </Grid>
      ) : undefined}
      {isFirmatario &&
      codeSubject !== CodeSubject.INTERPR &&
      !isDichiarante &&
      !isBaseOpFi &&
      !isBase02opfi &&
      !isConiopFi ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Comprensione"
              idDominio="32"
              required={!(requiredOnlyNameSurname || requiredOnlyName || nothingRequired)}
              value={generality?.conditionsItemSignatory?.id?.toString() || ''}
              onChange={handleListConditionsSignatoryChange}
              disabled={isBaseAnmd || disabled}
              error={
                Boolean(errorsGenerality?.conditionsItemSignatory) &&
                touchedGenerality?.conditionsItemSignatory
              }
            />
          </Grid>
          {/* {generality?.conditionsItemSignatory?.id?.toString() === ID_REASON_IMPEDIMENT ? (
            <Grid item xs={12} md={6}>
              <PmiTextField
                aria-label="Motivo impedimento scrittura"
                label="Motivo impedimento scrittura"
                id="reasonImpediment"
                name="reasonImpediment"
                fullWidth
                disabled={ isBaseAnmd ||disabled}
                helperText={errorsGenerality?.reasonImpedimentSignatory}
                error={
                  touchedGenerality?.reasonImpedimentSignatory &&
                  Boolean(errorsGenerality?.reasonImpedimentSignatory)
                }
                type="text"
                variant="outlined"
                value={generality?.reasonImpedimentSignatory || ''}
                onChange={(event) => {
                  onChangeGenerality('reasonImpedimentSignatory', event.target.value);
                }}
              />
            </Grid>
          ) : undefined} */}
        </Fragment>
      ) : undefined}
      {isDichiarante && !isRichUcopf ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Comprensione dichiarante"
              idDominio="32"
              required={!(requiredOnlyNameSurname || requiredOnlyName || nothingRequired)}
              value={generality?.conditionsItem?.id?.toString() || ''}
              onChange={handleListConditionsChange}
              disabled={isBaseAnmd || disabled}
              error={Boolean(errorsGenerality?.conditionsItem) && touchedGenerality?.conditionsItem}
            />
          </Grid>
          {generality?.conditionsItem?.id?.toString() === ID_REASON_IMPEDIMENT ? (
            <Grid item xs={12} md={6}>
              <PmiTextField
                aria-label="Motivo impedimento scrittura"
                label="Motivo impedimento scrittura"
                id="reasonImpediment"
                name="reasonImpediment"
                fullWidth
                disabled={isBaseAnmd || disabled}
                helperText={errorsGenerality?.reasonImpediment}
                error={
                  touchedGenerality?.reasonImpediment && Boolean(errorsGenerality?.reasonImpediment)
                }
                type="text"
                variant="outlined"
                value={generality?.reasonImpediment || ''}
                onChange={(event) => {
                  onChangeGenerality('reasonImpediment', event.target.value);
                }}
              />
            </Grid>
          ) : undefined}
        </Fragment>
      ) : undefined}
      {isBaseG ? (
        <Grid item xs={12} md={4}>
          <ListAnsc
            label="Grado di parentela"
            idDominio="43"
            value={generality?.relationship || ''}
            onChange={onChangeListRelationship}
            disabled={isBaseAnmd || disabled}
            required
            error={touchedGenerality?.relationship && Boolean(errorsGenerality?.relationship)}
          />
        </Grid>
      ) : undefined}
      {isWithInterpreteCheck ? (
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="transcript"
                checked={generality?.isWithInterprete || false}
                onChange={() => {
                  onChangeGenerality('isWithInterprete', !generality?.isWithInterprete);
                }}
                inputProps={{ 'aria-label': 'Presenza interprete' }}
                disabled={isBaseAnmd || disabled}
              />
            }
            label="Presenza interprete"
          />
        </Grid>
      ) : undefined}
      {isIntCit || isIntCit2 || isIntCit3 ? (
        <BaseIntCit
          onChangeGenerality={onChangeGenerality}
          errorsGenerality={errorsGenerality}
          generality={generality}
          nothingRequired={nothingRequired}
          requiredOnlyNameSurname={requiredOnlyNameSurname || requiredOnlyName}
          touchedGenerality={touchedGenerality}
          disabled={isBaseAnmd || disabled}
          isIntCit2={isIntCit2}
          isIntCit3={isIntCit3}
        />
      ) : undefined}
      {isBaseUc ? (
        <Grid item xs={12} md={4}>
          <NationalityList
            label="Ulteriore cittadinanza di cui si è in possesso"
            value={generality?.otherCitizenship || null}
            onChange={handleOtherCitizenshipListChange}
            required={!(requiredOnlyNameSurname || requiredOnlyName || nothingRequired)}
            disabled={isBaseAnmd || disabled}
            id="otherCitizenship"
            error={
              (touchedGenerality?.otherCitizenship as unknown as FormikTouched<NationalityVM>) &&
              Boolean(errorsGenerality?.otherCitizenship as FormikErrors<NationalityVM>)
            }
          />
        </Grid>
      ) : undefined}
      {isBasePerd ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Motivo perdita cittadinanza"
            idDominio="62"
            required={!(requiredOnlyNameSurname || requiredOnlyName || nothingRequired)}
            value={generality?.reasonLossCitizenship?.id?.toString() || ''}
            onChange={handleListReasonLossCitizenshipChange}
            disabled={isBaseAnmd || disabled}
            error={
              Boolean(errorsGenerality?.reasonLossCitizenship) &&
              touchedGenerality?.reasonLossCitizenship
            }
          />
        </Grid>
      ) : undefined}
      {isSignatoryCheck ? (
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="Firmatario"
                checked={generality?.isSignatory || false}
                onChange={() => {
                  if (isBaseOpFi || isBase02opfi || isConiopFi) {
                    onChangeFieldsGenerality({
                      isSignatory: !generality?.isSignatory,
                      conditionsItemSignatory: initialConditionsItem,
                    });

                    return;
                  }
                  onChangeGenerality('isSignatory', !generality?.isSignatory);
                }}
                inputProps={{ 'aria-label': 'Firmatario' }}
              />
            }
            label="Firmatario"
          />
        </Grid>
      ) : undefined}
      {(isBaseOpFi || isBase02opfi || isConiopFi || isRichUcopf) && generality?.isSignatory ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Comprensione"
            idDominio="32"
            value={generality?.conditionsItemSignatory?.id?.toString() || ''}
            onChange={handleListConditionsSignatoryChange}
            disabled={isBaseAnmd || disabled}
            error={
              Boolean(errorsGenerality?.conditionsItemSignatory) &&
              touchedGenerality?.conditionsItemSignatory
            }
          />
        </Grid>
      ) : undefined}
      {isSegrCiv ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Ruolo segretario"
            idDominio="91"
            value={generality?.roloSecretary || ''}
            onChange={handleListRoloSecretaryChange}
            disabled={isBaseAnmd || disabled}
            error={Boolean(errorsGenerality?.roloSecretary) && touchedGenerality?.roloSecretary}
            required
          />
        </Grid>
      ) : undefined}
      {isPericCiv || isBaseNoIntP || isNeosnNoIntP ? (
        <Fragment>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isImminentDangerOfLife"
                  checked={generality?.isImminentDangerOfLife || false}
                  disabled={isBaseAnmd || disabled}
                  onChange={() => {
                    onChangeFieldsGenerality({
                      isImminentDangerOfLife: !generality?.isImminentDangerOfLife,
                      verificationOfWill: '',
                    });
                  }}
                  inputProps={{ 'aria-label': 'In imminente pericolo di vita' }}
                />
              }
              label="In imminente pericolo di vita"
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label={`${
                isBaseNoIntP ? 'Mezzo con cui si accerta la volontà' : 'Accertamento volontà'
              } ${generality?.isImminentDangerOfLife ? '*' : ''}`}
              aria-label="Accertamento volontà"
              id="verificationOfWill"
              name="verificationOfWill"
              fullWidth
              disabled={isBaseAnmd || disabled || !generality?.isImminentDangerOfLife}
              helperText={errorsGenerality?.verificationOfWill}
              error={
                touchedGenerality?.verificationOfWill &&
                Boolean(errorsGenerality?.verificationOfWill)
              }
              type="text"
              variant="outlined"
              value={generality?.verificationOfWill || ''}
              onChange={(event) => {
                onChangeGenerality('verificationOfWill', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isBaseNoIntR ? (
        <Fragment>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isRecognizedGroom"
                  checked={generality?.isRecognizedGroom || false}
                  onChange={() => {
                    onChangeGenerality('isRecognizedGroom', !generality?.isRecognizedGroom);
                  }}
                  inputProps={{ 'aria-label': 'Figlio già riconosciuto dallo sposo' }}
                  disabled={isBaseAnmd || disabled}
                />
              }
              label="Figlio già riconosciuto dallo sposo"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isRecognizedWife"
                  checked={generality?.isRecognizedWife || false}
                  onChange={() => {
                    onChangeGenerality('isRecognizedWife', !generality?.isRecognizedWife);
                  }}
                  inputProps={{ 'aria-label': 'Figlio già riconosciuto dalla sposa' }}
                  disabled={isBaseAnmd || disabled}
                />
              }
              label="Figlio già riconosciuto dalla sposa"
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isBaseAvv3 || isBaseAvv4 ? (
        <FormBaseAvv
          disabled={isBaseAnmd || disabled}
          generality={generality}
          errorsGenerality={errorsGenerality}
          touchedGenerality={touchedGenerality}
          onChangeGenerality={onChangeGenerality}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          nothingRequired={nothingRequired}
          configuration={configuration}
          dateFormationAct={dateFormationAct}
        />
      ) : undefined}
      {isDichiarante && !birthDateIsMaggiorenne && Boolean(generality?.birthDate) && !isBase14 ? (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="isMinorDeclarant"
                checked={generality?.isMinorDeclarant || false}
                onChange={() => {
                  onChangeGenerality('isMinorDeclarant', !generality?.isMinorDeclarant);
                }}
                inputProps={{ 'aria-label': 'Il soggetto è autorizzato nonostante minorenne' }}
                disabled={isBaseAnmd || disabled}
              />
            }
            label="Il soggetto è autorizzato nonostante minorenne"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityBaseComponent.displayName = 'Form generality BASE component';

const FormGeneralityBase = memo(FormGeneralityBaseComponent);

export default FormGeneralityBase;
